import { createFileRoute } from "@tanstack/react-router";
import BlogPost from "@/pages/BlogPost";
import { getCuratedGuideBySlug } from "@/data/curatedGuides";
import { getBlogCoverImage } from "@/utils/blogImages";

const SUPABASE_URL = "https://fmhokzqxpqgficjyzvnr.supabase.co";
const SUPABASE_PUBLISHABLE_KEY =
  "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImZtaG9renF4cHFnZmljanl6dm5yIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjgyMzUwNzYsImV4cCI6MjA4MzgxMTA3Nn0.s-YDzTAa7hI4SumjEuTn6HRAaDNWep-U60E8QXTy7SE";

async function fetchBlogPostServer(slug: string) {
  if (!slug) return null;
  const cleanSlug = slug.toLowerCase().trim();

  // 1. Check curated guides first
  const curated = getCuratedGuideBySlug(cleanSlug);
  if (curated) return curated;

  // 2. Query Supabase REST API
  try {
    const url = `${SUPABASE_URL}/rest/v1/blog_posts?slug=eq.${encodeURIComponent(
      cleanSlug
    )}&is_published=eq.true&select=*`;
    const res = await fetch(url, {
      headers: {
        apikey: SUPABASE_PUBLISHABLE_KEY,
        Authorization: `Bearer ${SUPABASE_PUBLISHABLE_KEY}`,
        Accept: "application/json",
      },
    });
    if (!res.ok) return null;
    const list = await res.json();
    return list && list.length > 0 ? list[0] : null;
  } catch (err) {
    console.warn("[BlogPostRoute] Server fetch failed:", err);
    return null;
  }
}

export const Route = createFileRoute("/blog/$slug")({
  loader: async ({ params }) => {
    const post = await fetchBlogPostServer(params.slug);
    return { post };
  },
  head: ({ loaderData, params }) => {
    const post = loaderData?.post;
    const canonicalUrl = `https://mnre.in/blog/${params.slug}`;

    if (!post) {
      return {
        meta: [
          { title: "Blog Article | MNRE" },
          { name: "description", content: "Read this Mumbai real estate article from MNRE." },
          { property: "og:title", content: "Blog Article | MNRE" },
          { property: "og:description", content: "Read this Mumbai real estate article from MNRE." },
          { property: "og:type", content: "article" },
          { property: "og:url", content: canonicalUrl },
          { name: "twitter:card", content: "summary_large_image" },
        ],
        links: [{ rel: "canonical", href: canonicalUrl }],
      };
    }

    const title = post.meta_title || `${post.title} | MNRE`;
    const description = (post.meta_description || post.excerpt || "Read this article on MNRE.")
      .replace(/\s+/g, " ")
      .trim()
      .substring(0, 160);
    const coverImage = getBlogCoverImage(post);

    const articleJsonLd: any = {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: post.title,
      description: description,
      image: [coverImage],
      datePublished: post.published_at || post.created_at,
      dateModified: post.updated_at || post.published_at || post.created_at,
      author: {
        "@type": "Person",
        name: post.author_name || "Mohnish Nagpal",
      },
      publisher: {
        "@type": "Organization",
        name: "Mohnish Nagpal Real Estate",
        url: "https://mnre.in",
        logo: {
          "@type": "ImageObject",
          url: "https://mnre.in/lovable-uploads/abe5bbca-52aa-4071-ba12-16af51cf5fac.webp",
        },
      },
      mainEntityOfPage: {
        "@type": "WebPage",
        "@id": canonicalUrl,
      },
    };

    const breadcrumbJsonLd = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://mnre.in/" },
        { "@type": "ListItem", position: 2, name: "Blog", item: "https://mnre.in/blog" },
        ...(post.category
          ? [{ "@type": "ListItem", position: 3, name: post.category, item: "https://mnre.in/blog" }]
          : []),
        { "@type": "ListItem", position: post.category ? 4 : 3, name: post.title, item: canonicalUrl },
      ],
    };

    const scripts: any[] = [
      {
        type: "application/ld+json",
        children: JSON.stringify([articleJsonLd, breadcrumbJsonLd]),
      },
    ];

    if (post.faqs && Array.isArray(post.faqs) && post.faqs.length > 0) {
      const faqJsonLd = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: post.faqs.map((f: any) => ({
          "@type": "Question",
          name: f.question,
          acceptedAnswer: {
            "@type": "Answer",
            text: f.answer,
          },
        })),
      };
      scripts.push({
        type: "application/ld+json",
        children: JSON.stringify(faqJsonLd),
      });
    }

    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "article" },
        { property: "og:url", content: canonicalUrl },
        { property: "og:image", content: coverImage },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
        { name: "twitter:image", content: coverImage },
      ],
      links: [{ rel: "canonical", href: canonicalUrl }],
      scripts,
    };
  },
  component: BlogPostRoute,
});

function BlogPostRoute() {
  const data = Route.useLoaderData();
  const { slug } = Route.useParams();
  return <BlogPost initialPost={data?.post ?? null} slug={slug} />;
}
